import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import { PMButton } from "../../utils/PMButton";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { ModernProjectActivitySelectorField } from "../../utils/moduleSelectors/ModernProjectActivitySelectorField";
import { splitHour } from "../../../api/hour";

const ACCENT = {
  solid: "#1463C7",
  grad: "linear-gradient(135deg,#1463C7,#2D8BE8)",
  deep: "#0B3A78",
  mid: "#2D8BE8",
  heroBg: "linear-gradient(160deg,#EAF3FD,#F2F8FE)",
  heroBorder: "#BFDCF7",
};

const SUM_TOLERANCE = 0.001;

let lineKeySeq = 0;
function nextLineKey() {
  lineKeySeq += 1;
  return `split-line-${lineKeySeq}`;
}

function parseTime(value) {
  const n =
    typeof value === "number"
      ? value
      : parseFloat(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function roundTime(value) {
  return Math.round(parseTime(value) * 1000) / 1000;
}

function formatTime(value) {
  const n = roundTime(value);
  return Number.isInteger(n) ? String(n) : String(n);
}

function emptyLine(overrides = {}) {
  return {
    key: nextLineKey(),
    project_id: null,
    project: null,
    project_activity_id: null,
    project_activity: null,
    time: "",
    description: "",
    ...overrides,
  };
}

/** Prefer first row's project; fall back to first row that has a project set. */
function projectFromLines(lines) {
  const source =
    (lines || []).find(
      (line) => line?.project_id != null && line.project_id !== ""
    ) || null;
  if (!source) return {};
  return {
    project_id: source.project_id,
    project: source.project || null,
  };
}

function lineFromHour(hour) {
  const project = hour?.project || null;
  const projectActivity = hour?.project_activity || null;
  const projectId =
    project?.id ?? hour?.project_id ?? projectActivity?.project_id ?? null;
  const activityId = projectActivity?.id ?? hour?.project_activity_id ?? null;

  return emptyLine({
    project_id: projectId != null ? String(projectId) : null,
    project,
    project_activity_id: activityId != null ? String(activityId) : null,
    project_activity: projectActivity,
    time: hour?.time != null ? formatTime(hour.time) : "",
    description: hour?.description || "",
  });
}

/**
 * Split one hour registration across multiple project activities.
 * POST /hours/:id/split — sum of line times must match original.time.
 */
export const HoursSplitDialog = ({ open, onClose, hour, onDone }) => {
  const { t } = useTranslation();
  const [lines, setLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const totalTime = roundTime(hour?.time);

  useEffect(() => {
    if (!open || !hour?.id) return;
    const first = lineFromHour(hour);
    setLines([
      first,
      emptyLine({
        ...projectFromLines([first]),
      }),
    ]);
    setSubmitting(false);
  }, [open, hour?.id]);

  const allocatedSum = useMemo(
    () => roundTime(lines.reduce((sum, line) => sum + parseTime(line.time), 0)),
    [lines]
  );

  const sumMatches = Math.abs(allocatedSum - totalTime) <= SUM_TOLERANCE;
  const remaining = roundTime(totalTime - allocatedSum);

  const rowsValid = lines.every((line) => {
    const time = parseTime(line.time);
    return (
      line.project_activity_id != null &&
      line.project_activity_id !== "" &&
      time > 0
    );
  });

  const canSave =
    !submitting &&
    hour?.id &&
    lines.length >= 2 &&
    rowsValid &&
    sumMatches;

  const updateLine = (key, patch) => {
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...patch } : line))
    );
  };

  const removeLine = (key) => {
    setLines((prev) =>
      prev.length <= 2 ? prev : prev.filter((l) => l.key !== key)
    );
  };

  const addLine = () => {
    const fill = remaining > SUM_TOLERANCE ? formatTime(remaining) : "";
    setLines((prev) => [
      ...prev,
      emptyLine({
        ...projectFromLines(prev),
        time: fill,
      }),
    ]);
  };

  const handleSubmit = async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      const payload = {
        lines: lines.map((line) => ({
          project_activity_id: parseInt(line.project_activity_id, 10),
          time: roundTime(line.time),
          ...(line.description?.trim()
            ? { description: line.description.trim() }
            : {}),
        })),
      };
      const body = await splitHour(hour.id, payload);
      const data = body?.data || body;
      toast.success(
        t("hours.split.success", {
          count: Array.isArray(data?.hours) ? data.hours.length : lines.length,
        })
      );
      if (typeof onDone === "function") onDone(data);
      onClose();
    } catch (e) {
      const apiErrors = e?.response?.data?.errors;
      const msg =
        (Array.isArray(apiErrors) && apiErrors[0]) ||
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        t("hours.split.error");
      toast.error(typeof msg === "string" ? msg : t("hours.split.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const sumTone = sumMatches
    ? { bg: "#F0FDF4", border: "#BBF7D0", text: "#0B7A3B" }
    : { bg: "#FEF2F2", border: "#FECACA", text: "#DC2626" };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("hours.split.title")}
      size="4xl"
      zIndex={2600}
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 leading-relaxed">
          {t("hours.split.intro")}
        </p>

        <div
          className="rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
          style={{
            background: ACCENT.heroBg,
            border: `1.5px solid ${ACCENT.heroBorder}`,
          }}
          role="status"
          aria-live="polite"
        >
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("hours.split.total_label")}
            </div>
            <div
              className="text-2xl font-extrabold tabular-nums"
              style={{ color: ACCENT.deep }}
            >
              {formatTime(totalTime)}
              <span
                className="text-base font-semibold ml-1"
                style={{ color: ACCENT.mid }}
              >
                {t("hours.split.unit")}
              </span>
            </div>
          </div>
          <div
            className="rounded-xl px-3 py-2 text-sm font-semibold tabular-nums"
            style={{
              background: sumTone.bg,
              border: `1px solid ${sumTone.border}`,
              color: sumTone.text,
            }}
          >
            {t("hours.split.sum_live", {
              allocated: formatTime(allocatedSum),
              total: formatTime(totalTime),
            })}
            {!sumMatches && (
              <div className="text-xs font-medium mt-0.5 opacity-90">
                {remaining > 0
                  ? t("hours.split.remaining", {
                      hours: formatTime(remaining),
                    })
                  : t("hours.split.over_allocated", {
                      hours: formatTime(Math.abs(remaining)),
                    })}
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2.5 font-semibold">
                  {t("hours.split.col_project")}
                </th>
                <th className="px-3 py-2.5 font-semibold">
                  {t("hours.split.col_activity")}
                </th>
                <th className="px-3 py-2.5 font-semibold w-28">
                  {t("hours.split.col_time")}
                </th>
                <th className="px-3 py-2.5 font-semibold">
                  {t("hours.split.col_description")}
                </th>
                <th className="px-3 py-2.5 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lines.map((line, index) => (
                <SplitLineRow
                  key={line.key}
                  line={line}
                  index={index}
                  canRemove={lines.length > 2}
                  onChange={updateLine}
                  onRemove={removeLine}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-3">
          {lines.map((line, index) => (
            <div
              key={line.key}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-3 space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500">
                  {t("hours.split.row_n", { n: index + 1 })}
                </span>
                {lines.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeLine(line.key)}
                    className="inline-flex items-center justify-center h-10 w-10 rounded-xl text-slate-500 hover:bg-white hover:text-rose-600"
                    aria-label={t("hours.split.remove_row")}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
              <SplitLineFields line={line} onChange={updateLine} />
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addLine}
          className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50"
        >
          <PlusIcon className="h-5 w-5" style={{ color: ACCENT.solid }} />
          {t("hours.split.add_row")}
        </button>

        {!sumMatches && (
          <p className="text-sm text-rose-600">
            {t("hours.split.mismatch_hint")}
          </p>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-slate-100">
          <PMButton
            type="button"
            className="bg-slate-200 text-slate-800 min-h-[44px]"
            onClick={onClose}
            disabled={submitting}
          >
            {t("Avbryt")}
          </PMButton>
          <PMButton
            type="button"
            onClick={handleSubmit}
            disabled={!canSave}
            className="min-h-[44px] text-white"
            style={
              canSave
                ? {
                    background: ACCENT.grad,
                    boxShadow: "0 12px 24px -8px rgba(20,99,199,.6)",
                  }
                : {
                    background: "#E2E8F0",
                    color: "#94A3B8",
                    boxShadow: "none",
                    cursor: "not-allowed",
                  }
            }
          >
            {submitting ? t("hours.split.saving") : t("hours.split.submit")}
          </PMButton>
        </div>
      </div>
    </PMDialog>
  );
};

function SplitLineRow({ line, index, canRemove, onChange, onRemove }) {
  const { t } = useTranslation();
  return (
    <tr className="align-top">
      <td className="px-3 py-3 min-w-[180px]">
        <SplitProjectField line={line} onChange={onChange} />
      </td>
      <td className="px-3 py-3 min-w-[200px]">
        <SplitActivityField line={line} onChange={onChange} />
      </td>
      <td className="px-3 py-3">
        <input
          type="number"
          inputMode="decimal"
          step="0.25"
          min="0"
          value={line.time}
          onChange={(e) => onChange(line.key, { time: e.target.value })}
          className="w-full min-h-[44px] rounded-xl border-[1.5px] border-slate-200 bg-white px-3 text-base font-semibold text-slate-900 tabular-nums focus:outline-none focus:border-blue-500"
          aria-label={`${t("hours.split.col_time")} ${index + 1}`}
        />
      </td>
      <td className="px-3 py-3 min-w-[160px]">
        <input
          type="text"
          value={line.description}
          onChange={(e) => onChange(line.key, { description: e.target.value })}
          placeholder={t("hours.split.description_placeholder")}
          className="w-full min-h-[44px] rounded-xl border-[1.5px] border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:border-blue-500"
        />
      </td>
      <td className="px-2 py-3">
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(line.key)}
            className="inline-flex items-center justify-center h-10 w-10 rounded-xl text-slate-400 hover:bg-rose-50 hover:text-rose-600"
            aria-label={t("hours.split.remove_row")}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        )}
      </td>
    </tr>
  );
}

function SplitLineFields({ line, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-bold text-slate-600 mb-1">
          {t("hours.split.col_project")}
        </label>
        <SplitProjectField line={line} onChange={onChange} />
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-600 mb-1">
          {t("hours.split.col_activity")}
        </label>
        <SplitActivityField line={line} onChange={onChange} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-bold text-slate-600 mb-1">
            {t("hours.split.col_time")}
          </label>
          <input
            type="number"
            inputMode="decimal"
            step="0.25"
            min="0"
            value={line.time}
            onChange={(e) => onChange(line.key, { time: e.target.value })}
            className="w-full min-h-[44px] rounded-xl border-[1.5px] border-slate-200 bg-white px-3 text-base font-semibold text-slate-900 tabular-nums focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-bold text-slate-600 mb-1">
            {t("hours.split.col_description")}
          </label>
          <input
            type="text"
            value={line.description}
            onChange={(e) =>
              onChange(line.key, { description: e.target.value })
            }
            placeholder={t("hours.split.description_placeholder")}
            className="w-full min-h-[44px] rounded-xl border-[1.5px] border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>
    </div>
  );
}

function SplitProjectField({ line, onChange }) {
  const setFieldValue = (field, value) => {
    if (field === "project_id") {
      onChange(line.key, {
        project_id: value,
        project_activity_id: null,
        project_activity: null,
        ...(value == null || value === "" ? { project: null } : {}),
      });
    }
  };

  return (
    <ProjectSelector
      name={`split_project_${line.key}`}
      selected={line.project || undefined}
      setFieldValue={setFieldValue}
      values={{ project_id: line.project_id }}
      errors={{}}
      noLabel
      zIndex={2800}
      onChange={(e) => {
        if (e?.target?.object) {
          onChange(line.key, {
            project: e.target.object,
            project_id:
              e.target.object.id != null ? String(e.target.object.id) : null,
            project_activity_id: null,
            project_activity: null,
          });
        }
      }}
    />
  );
}

function SplitActivityField({ line, onChange }) {
  const fieldName = `split_activity_${line.key}`;
  const setFieldValue = (field, value) => {
    if (field === "project_activity") {
      onChange(line.key, {
        project_activity: value,
        ...(value?.id != null
          ? { project_activity_id: String(value.id) }
          : value == null
            ? { project_activity_id: null }
            : {}),
      });
      return;
    }
    if (field === fieldName || field === "project_activity_id") {
      onChange(line.key, {
        project_activity_id: value,
        ...(value == null || value === "" ? { project_activity: null } : {}),
      });
    }
  };

  return (
    <ModernProjectActivitySelectorField
      name={fieldName}
      label={false}
      selected={line.project_activity}
      project={line.project}
      setFieldValue={setFieldValue}
      errors={{}}
      onChange={(e) => {
        const obj = e?.target?.object;
        if (obj?.id != null) {
          onChange(line.key, {
            project_activity: obj,
            project_activity_id: String(obj.id),
          });
        } else if (e?.target?.value == null) {
          onChange(line.key, {
            project_activity: null,
            project_activity_id: null,
          });
        }
      }}
    />
  );
}
